import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, TrendingUp } from "lucide-react";
import { MaskLines } from "./Reveal";
import { STATS } from "../../data/site";

const EASE = [0.22, 1, 0.36, 1];

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative" data-testid="hero-tilt-card">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0 round 2rem)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 2rem)" }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.35 }}
          className="relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-muted"
        >
          <motion.img src="/img/team-candid.webp" alt="The Catalyst team planning a campaign together" width="1600" height="1067"
            initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: EASE, delay: 0.35 }}
            className="h-full w-full object-cover" fetchpriority="high" />
          <div className="absolute inset-0 bg-gradient-to-tr from-navy/40 via-transparent to-transparent" />
        </motion.div>
        <motion.div style={{ transform: "translateZ(70px)" }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8, ease: EASE }}
          className="absolute -left-4 bottom-10 rounded-2xl bg-white p-4 shadow-[0_30px_60px_-20px_rgba(17,24,39,0.35)] sm:-left-10" data-testid="hero-metric-card">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-flame text-white"><TrendingUp className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-2xl font-bold leading-none text-ink">+3.4x</p>
              <p className="mt-1 text-xs font-medium text-ink/65">qualified leads, 90 days</p>
            </div>
          </div>
        </motion.div>
        <motion.div style={{ transform: "translateZ(110px)" }} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4, duration: 0.8, ease: EASE }}
          className="absolute -right-3 top-8 grid h-24 w-24 place-items-center rounded-full bg-ink text-center text-canvas sm:-right-8 sm:h-28 sm:w-28">
          <span className="font-display text-xs font-semibold uppercase leading-tight tracking-wider">Canberra<br />& region</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const ty = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="home-hero">
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-flame/[0.07] blur-3xl" aria-hidden="true" />
      <div className="wrap relative grid items-center gap-14 pb-16 pt-10 sm:pt-16 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-20">
        <motion.div style={{ y: ty }} className="lg:col-span-7">
          <motion.span className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
            Canberra marketing, minus the fluff
          </motion.span>
          <MaskLines
            testId="hero-headline"
            className="mt-6 font-display text-[2.9rem] font-extrabold leading-[0.95] tracking-[-0.035em] text-ink sm:text-7xl lg:text-[5.6rem] xl:text-[6.3rem]"
            lines={[
              "We create",
              <span key="m" className="relative inline-block text-flame">
                momentum
                <motion.svg viewBox="0 0 300 20" className="absolute -bottom-1 left-0 h-3 w-full sm:h-4" preserveAspectRatio="none" aria-hidden="true">
                  <motion.path d="M2 14 C 80 2, 200 2, 298 12" fill="none" stroke="#1D3557" strokeWidth="5" strokeLinecap="round"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.1, duration: 1, ease: EASE }} />
                </motion.svg>
              </span>,
              "for ambitious",
              "brands.",
            ]}
            delay={0.15}
          />
          <motion.p className="lede mt-8 max-w-xl" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease: EASE }}>
            Strategy, content, SEO and ads for Canberra businesses that would rather be busy than famous. Though, if you insist, we can do both.
          </motion.p>
          <motion.div className="mt-10 flex flex-wrap items-center gap-3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: EASE }}>
            <Link to="/contact" className="btn-primary !px-7 !py-4 text-base" data-testid="hero-primary-cta">Book a discovery call <ArrowUpRight className="h-5 w-5" /></Link>
            <Link to="/work" className="btn-ghost !px-7 !py-4 text-base" data-testid="hero-secondary-cta">See the proof <ArrowDownRight className="h-5 w-5" /></Link>
          </motion.div>
          <motion.dl className="mt-14 grid max-w-xl grid-cols-3 gap-6 border-t border-ink/10 pt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}>
            {STATS.map(([n, l]) => (
              <div key={l}>
                <dt className="sr-only">{l}</dt>
                <dd className="font-display text-2xl font-bold text-ink sm:text-3xl">{n}</dd>
                <p className="mt-1 text-xs leading-snug text-ink/65 sm:text-sm">{l}</p>
              </div>
            ))}
          </motion.dl>
        </motion.div>
        <motion.div style={{ y }} className="mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none">
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
